import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase.js';
import { GRADE_SUBJECT_PAIRS, scopesCover } from './catalogue';
import logger from './logger';

/**
 * Live list of users with the moderator role, plus scope coverage.
 * Administrators only — the Firestore rules deny other roles.
 *
 * @param {boolean} enabled subscribe only when true (i.e. for admins)
 */
export default function useModerators(enabled = true) {
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setModerators([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const q = query(collection(db, 'users'), where('role', '==', 'moderator'));
    return onSnapshot(
      q,
      (snapshot) => {
        setModerators(
          snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || ''))
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        logger.error('Error loading moderators:', err);
        setError(err.code || 'unknown');
        setLoading(false);
      }
    );
  }, [enabled]);

  /** "Grade10/Mathematics" → moderators covering that collection */
  const coverage = useMemo(() => {
    const map = {};
    GRADE_SUBJECT_PAIRS.forEach(({ grade, subject }) => {
      map[`${grade}/${subject}`] = moderators.filter((m) => scopesCover(m.moderationScopes, subject, grade));
    });
    return map;
  }, [moderators]);

  return { moderators, coverage, loading, error };
}

export const moderatorName = (m) => m?.full_name || m?.email || 'Unnamed moderator';
